import React from 'react';

import {alpha3ToAlpha2} from 'src/utils';

const getCountryCode = (country: string) =>
  country.length === 3 ? alpha3ToAlpha2(country) : country;

const loadCountryFlag = (country: string) =>
  React.lazy(async () => {
    try {
      return await import(`@edna/country-flags/country-flags/${getCountryCode(country)}.svg?react`);
    } catch (e) {
      return {default: () => null};
    }
  });

// Note: use result icons with React.Suspense
export const useCountryFlags = <T extends {country: string} | string>(countries: T[] = []) => {
  const [countryFlags, setCountryFlags] = React.useState<Record<string, TSvgComponentType>>({});
  const flagsCacheRef = React.useRef<Record<string, TSvgComponentType>>({});

  const collectCountryFlags = React.useCallback(async () => {
    if (!countries.length) {
      return;
    }

    const newFlags: Record<string, TSvgComponentType> = {};

    try {
      await Promise.allSettled(
        countries.map(async (item) => {
          const country = typeof item === 'string' ? item : item.country;

          if (flagsCacheRef.current[country]) {
            return;
          }

          const flag = loadCountryFlag(country);

          flagsCacheRef.current[country] = flag;
          newFlags[country] = flag;
        }),
      );

      if (Object.keys(newFlags).length) {
        setCountryFlags((flags) => ({...flags, ...newFlags}));
      }
    } catch (e) {}
  }, [JSON.stringify(countries)]);

  React.useEffect(() => {
    if (!countries.length) {
      return;
    }

    collectCountryFlags();
  }, [JSON.stringify(countries)]);

  return countryFlags;
};
